<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAxios } from 'dashboard/composables/useAxios';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { Room, RoomEvent, createLocalTracks } from 'livekit-client';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const router = useRouter();
const axios = useAxios();
const { t } = useI18n();

const voiceAgentId = route.params.voiceAgentId;
const voiceAgent = ref(null);
const isFetching = ref(false);
const isConnecting = ref(false);
const isConnected = ref(false);
const room = ref(null);
const remoteAudioContainer = ref(null);
const isMicEnabled = ref(false);
const localAudioTrack = ref(null);
const hasRemoteParticipant = ref(false);
const isVideoEnabled = ref(false);
const isScreenSharing = ref(false);
const localVideoTrack = ref(null);
const localScreenTrack = ref(null);
const localVideoContainer = ref(null);

const fetchVoiceAgent = async () => {
  isFetching.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}`
    );
    voiceAgent.value = data;
  } catch (error) {
    console.error('Error fetching voice agent:', error);
    useAlert(t('AI_VOICE.PLAYGROUND.ERROR_FETCHING'));
  } finally {
    isFetching.value = false;
  }
};

const attachTrack = (track, container) => {
  if (!container) return;
  const element = track.attach();
  container.appendChild(element);
};

const startPlayground = async () => {
  isConnecting.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}/playground`,
      { test_mode: true }
    );

    // Connect to LiveKit room
    room.value = new Room();

    // Set up event listeners
    room.value.on(
      RoomEvent.TrackSubscribed,
      (track, publication, participant) => {
        console.log(
          'Playground: Track subscribed:',
          track.kind,
          'from:',
          participant.identity
        );
        if (track.kind === 'audio') {
          attachTrack(track, remoteAudioContainer.value);
          hasRemoteParticipant.value = true;
        }
      }
    );

    room.value.on(RoomEvent.TrackUnsubscribed, track => {
      console.log('Playground: Track unsubscribed:', track.kind);
      track.detach();
      hasRemoteParticipant.value = false;
    });

    room.value.on(RoomEvent.Disconnected, () => {
      console.log('Playground: Disconnected from room');
      isConnected.value = false;
      hasRemoteParticipant.value = false;
    });

    // Connect to room
    await room.value.connect(data.livekit_url, data.token);
    console.log('Playground: Connected to room');
    isConnected.value = true;

    // Auto-enable microphone
    await toggleMicrophone();
  } catch (error) {
    console.error('Error starting playground:', error);
    useAlert(t('AI_VOICE.PLAYGROUND.START_ERROR'));
  } finally {
    isConnecting.value = false;
  }
};

const toggleMicrophone = async () => {
  if (!room.value) return;

  try {
    if (isMicEnabled.value) {
      // Turn off microphone
      if (localAudioTrack.value) {
        await room.value.localParticipant.unpublishTrack(localAudioTrack.value);
        localAudioTrack.value.stop();
        localAudioTrack.value = null;
      }
      isMicEnabled.value = false;
    } else {
      // Turn on microphone
      const tracks = await createLocalTracks({ audio: true });
      const audioTrack = tracks[0];
      await room.value.localParticipant.publishTrack(audioTrack);
      localAudioTrack.value = audioTrack;
      isMicEnabled.value = true;
    }
  } catch (err) {
    console.error('Microphone toggle error:', err);
    useAlert(t('AI_VOICE.PLAYGROUND.SESSION_ERROR'));
  }
};

const toggleVideo = async () => {
  if (!room.value) return;

  try {
    if (isVideoEnabled.value) {
      // Turn off video
      if (localVideoTrack.value) {
        await room.value.localParticipant.unpublishTrack(localVideoTrack.value);
        localVideoTrack.value.stop();
        localVideoTrack.value = null;
      }
      isVideoEnabled.value = false;
    } else {
      // Turn on video
      const tracks = await createLocalTracks({ video: true });
      const videoTrack = tracks[0];
      await room.value.localParticipant.publishTrack(videoTrack);

      // Attach to local preview
      if (localVideoContainer.value) {
        attachTrack(videoTrack, localVideoContainer.value);
      }

      localVideoTrack.value = videoTrack;
      isVideoEnabled.value = true;
    }
  } catch (err) {
    console.error('Video toggle error:', err);
    useAlert('Failed to toggle camera. Please check camera permissions.');
  }
};

const toggleScreenShare = async () => {
  if (!room.value) return;

  try {
    if (isScreenSharing.value) {
      // Stop screen sharing
      if (localScreenTrack.value) {
        await room.value.localParticipant.unpublishTrack(
          localScreenTrack.value
        );
        localScreenTrack.value.stop();
        localScreenTrack.value = null;
      }
      isScreenSharing.value = false;
    } else {
      // Start screen sharing - use navigator.mediaDevices.getDisplayMedia
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
      });

      const screenTrack = stream.getVideoTracks()[0];

      // Wrap in LiveKit track and publish
      const { LocalVideoTrack } = await import('livekit-client');
      const localTrack = new LocalVideoTrack(screenTrack);
      await room.value.localParticipant.publishTrack(localTrack);

      localScreenTrack.value = localTrack;
      isScreenSharing.value = true;

      // Listen for when user stops sharing via browser UI
      screenTrack.onended = () => {
        isScreenSharing.value = false;
        localScreenTrack.value = null;
      };
    }
  } catch (err) {
    console.error('Screen share toggle error:', err);
    if (err.name === 'NotAllowedError') {
      useAlert('Screen sharing permission denied.');
    } else {
      useAlert('Failed to start screen sharing.');
    }
  }
};

const endPlayground = async () => {
  try {
    if (room.value) {
      await room.value.disconnect();
      room.value = null;
    }

    isConnected.value = false;
    isMicEnabled.value = false;
    isVideoEnabled.value = false;
    isScreenSharing.value = false;
    hasRemoteParticipant.value = false;
    localAudioTrack.value = null;
    localVideoTrack.value = null;
    localScreenTrack.value = null;
  } catch (error) {
    console.error('Error ending playground:', error);
    useAlert(t('AI_VOICE.PLAYGROUND.END_ERROR'));
  }
};

const voiceLanguageName = computed(() => {
  const languageMap = {
    multilingual: 'Multilingual',
    'en-US': 'English (US)',
    'en-GB': 'English (UK)',
    'es-ES': 'Spanish (Spain)',
    'es-MX': 'Spanish (Mexico)',
    'fr-FR': 'French',
    'de-DE': 'German',
    'it-IT': 'Italian',
    'pt-BR': 'Portuguese (Brazil)',
    'pt-PT': 'Portuguese (Portugal)',
    'nl-NL': 'Dutch',
    'pl-PL': 'Polish',
    'ru-RU': 'Russian',
    'ja-JP': 'Japanese',
    'ko-KR': 'Korean',
    'zh-CN': 'Chinese (Simplified)',
    'zh-TW': 'Chinese (Traditional)',
    'hi-IN': 'Hindi',
    'ar-SA': 'Arabic',
  };
  return languageMap[voiceAgent.value?.language] || voiceAgent.value?.language;
});

onMounted(() => {
  fetchVoiceAgent();
});

onBeforeUnmount(() => {
  if (isConnected.value && room.value) {
    room.value.disconnect();
  }
});
</script>

<template>
  <PageLayout
    :header-title="
      voiceAgent?.name ? `${voiceAgent.name} - Playground` : 'Voice Playground'
    "
    :show-pagination-footer="false"
    :is-fetching="isFetching"
    :show-know-more="false"
    :back-url="{ name: 'aivoice_voice_agents_edit', params: { voiceAgentId } }"
  >
    <template #body>
      <div v-if="voiceAgent" class="flex flex-col gap-6">
        <!-- Header Section -->
        <div class="flex flex-col gap-2">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('AI_VOICE.PLAYGROUND.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('AI_VOICE.PLAYGROUND.DESCRIPTION') }}
          </p>
        </div>

        <div class="flex gap-6">
          <!-- Left Column: Voice Agent Info & Instructions -->
          <div class="flex flex-col gap-6 w-96">
            <!-- Voice Agent Info Card -->
            <div
              class="flex flex-col gap-4 p-4 bg-n-solid-2 border border-n-weak rounded-lg"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('AI_VOICE.PLAYGROUND.AGENT_INFO') }}
              </h3>

              <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                  <span class="text-xs text-n-slate-11">{{
                    t('AI_VOICE.PLAYGROUND.LANGUAGE')
                  }}</span>
                  <span class="text-sm text-n-slate-12">{{
                    voiceLanguageName
                  }}</span>
                </div>

                <div class="flex flex-col gap-1">
                  <span class="text-xs text-n-slate-11">{{
                    t('AI_VOICE.PLAYGROUND.VOICE')
                  }}</span>
                  <span class="text-sm text-n-slate-12 capitalize">{{
                    voiceAgent.voice_id
                  }}</span>
                </div>

                <div v-if="voiceAgent.description" class="flex flex-col gap-1">
                  <span class="text-xs text-n-slate-11">{{
                    t('AI_VOICE.PLAYGROUND.DESCRIPTION')
                  }}</span>
                  <span class="text-sm text-n-slate-12">{{
                    voiceAgent.description
                  }}</span>
                </div>

                <div
                  v-if="voiceAgent.system_prompt"
                  class="flex flex-col gap-1"
                >
                  <span class="text-xs text-n-slate-11">{{
                    t('AI_VOICE.PLAYGROUND.SYSTEM_PROMPT')
                  }}</span>
                  <span class="text-sm text-n-slate-12 line-clamp-3">{{
                    voiceAgent.system_prompt
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Instructions Card -->
            <div
              class="flex flex-col gap-4 p-4 bg-n-solid-2 border border-n-weak rounded-lg"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('AI_VOICE.PLAYGROUND.HOW_TO_USE') }}
              </h3>

              <ol
                class="flex flex-col gap-3 text-sm text-n-slate-11 list-decimal list-inside"
              >
                <li>{{ t('AI_VOICE.PLAYGROUND.STEP_1') }}</li>
                <li>{{ t('AI_VOICE.PLAYGROUND.STEP_2') }}</li>
                <li>{{ t('AI_VOICE.PLAYGROUND.STEP_3') }}</li>
                <li>{{ t('AI_VOICE.PLAYGROUND.STEP_4') }}</li>
              </ol>
            </div>

            <!-- Session Controls -->
            <div class="flex flex-col gap-3">
              <Button
                v-if="!isConnected"
                color="blue"
                size="md"
                :is-loading="isConnecting"
                :disabled="isConnecting"
                class="w-full"
                @click="startPlayground"
              >
                <span class="i-lucide-phone mr-2" />
                {{ t('AI_VOICE.PLAYGROUND.START_SESSION') }}
              </Button>

              <Button
                v-else
                color="ruby"
                size="md"
                class="w-full"
                @click="endPlayground"
              >
                <span class="i-lucide-phone-off mr-2" />
                {{ t('AI_VOICE.PLAYGROUND.END_SESSION') }}
              </Button>

              <p v-if="isConnected" class="text-xs text-center text-n-slate-11">
                {{ t('AI_VOICE.PLAYGROUND.SESSION_ACTIVE') }}
              </p>
            </div>
          </div>

          <!-- Right Column: Voice Session UI -->
          <div class="flex flex-col flex-1 gap-4">
            <div
              class="flex flex-col items-center justify-center min-h-[600px] p-8 bg-n-solid-2 border border-n-weak rounded-lg"
            >
              <div
                v-if="!isConnected"
                class="flex flex-col items-center gap-4 text-center"
              >
                <div
                  class="flex items-center justify-center w-16 h-16 bg-n-alpha-3 rounded-full"
                >
                  <span class="i-lucide-mic text-3xl text-n-slate-11" />
                </div>
                <div class="flex flex-col gap-2">
                  <h3 class="text-base font-semibold text-n-slate-12">
                    {{ t('AI_VOICE.PLAYGROUND.NOT_STARTED') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 max-w-md">
                    {{ t('AI_VOICE.PLAYGROUND.NOT_STARTED_DESC') }}
                  </p>
                </div>
              </div>

              <!-- Active Voice Session -->
              <div v-else class="flex flex-col items-center gap-6 w-full">
                <div
                  class="flex items-center justify-center w-24 h-24 bg-blue-500 rounded-full animate-pulse"
                >
                  <span class="i-lucide-phone text-5xl text-white" />
                </div>

                <div class="flex flex-col items-center gap-2">
                  <h3 class="text-lg font-semibold text-n-slate-12">
                    Voice Session Active
                  </h3>
                  <p class="text-sm text-n-slate-11">
                    {{
                      hasRemoteParticipant
                        ? 'Connected to voice agent'
                        : 'Waiting for voice agent...'
                    }}
                  </p>
                </div>

                <!-- Media Controls -->
                <div class="flex flex-col gap-6">
                  <!-- Microphone Control -->
                  <div class="flex items-center gap-3">
                    <button
                      class="flex items-center justify-center w-16 h-16 rounded-full transition-all"
                      :class="
                        isMicEnabled
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-red-500 hover:bg-red-600'
                      "
                      @click="toggleMicrophone"
                    >
                      <span
                        class="text-2xl text-white"
                        :class="
                          isMicEnabled ? 'i-lucide-mic' : 'i-lucide-mic-off'
                        "
                      />
                    </button>
                    <span class="text-sm text-n-slate-11">
                      {{ isMicEnabled ? 'Microphone On' : 'Microphone Off' }}
                    </span>
                  </div>

                  <!-- Video Control -->
                  <div class="flex items-center gap-3">
                    <button
                      class="flex items-center justify-center w-16 h-16 rounded-full transition-all"
                      :class="
                        isVideoEnabled
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-gray-500 hover:bg-gray-600'
                      "
                      @click="toggleVideo"
                    >
                      <span
                        class="text-2xl text-white"
                        :class="
                          isVideoEnabled
                            ? 'i-lucide-video'
                            : 'i-lucide-video-off'
                        "
                      />
                    </button>
                    <span class="text-sm text-n-slate-11">
                      {{ isVideoEnabled ? 'Camera On' : 'Camera Off' }}
                    </span>
                  </div>

                  <!-- Screen Share Control -->
                  <div class="flex items-center gap-3">
                    <button
                      class="flex items-center justify-center w-16 h-16 rounded-full transition-all"
                      :class="
                        isScreenSharing
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-gray-500 hover:bg-gray-600'
                      "
                      @click="toggleScreenShare"
                    >
                      <span
                        class="text-2xl text-white"
                        :class="
                          isScreenSharing
                            ? 'i-lucide-monitor-check'
                            : 'i-lucide-monitor'
                        "
                      />
                    </button>
                    <span class="text-sm text-n-slate-11">
                      {{ isScreenSharing ? 'Sharing Screen' : 'Share Screen' }}
                    </span>
                  </div>
                </div>

                <!-- Local Video Preview -->
                <div
                  v-if="isVideoEnabled"
                  class="relative w-64 h-48 bg-n-solid-3 rounded-lg overflow-hidden border-2 border-blue-500"
                >
                  <div ref="localVideoContainer" class="w-full h-full" />
                  <div
                    class="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white"
                  >
                    Your Camera
                  </div>
                </div>

                <!-- Hidden audio container for remote participant -->
                <div ref="remoteAudioContainer" style="display: none" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
